import React, { useState } from 'react';
import * as api from '../api';

function SavedShows({ shows, onCreateShow, onOpenShow, onDeleteShow, onDuplicateShow, onRenameShow, currentShowId, currentUser }) {
  const [showNewModal, setShowNewModal] = useState(false);
  const [newShowName, setNewShowName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [renamingShow, setRenamingShow] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [showDeletedModal, setShowDeletedModal] = useState(false);

  const filteredShows = shows.filter(show =>
    show.name.toLowerCase().includes(searchText.toLowerCase()) ||
    (show.jobInfo?.jobTitle || '').toLowerCase().includes(searchText.toLowerCase()) ||
    (show.jobInfo?.showDates || '').toLowerCase().includes(searchText.toLowerCase())
  );

  const handleRename = () => {
    if (!renameValue.trim()) return;
    onRenameShow(renamingShow, renameValue.trim());
    setRenamingShow(null);
    setRenameValue('');
  };

  const handleCreate = () => {
    if (!newShowName.trim()) return;
    onCreateShow(newShowName.trim());
    setNewShowName('');
    setShowNewModal(false);
  };

  return (
    <div>
      <div className="page-header">
        <h1>Saved Shows</h1>
        <p>All of your saved show projects. Create a new show or open an existing one.</p>
      </div>

      {/* Controls Row */}
      <div style={{ marginBottom: '24px', display: 'flex', gap: '12px', alignItems: 'center' }}>
        {currentUser?.role !== 'viewer' && (
          <button
            onClick={() => setShowNewModal(true)}
            style={{
              backgroundColor: '#4299e1',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 24px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            + New Show
          </button>
        )}
        <input
          type="text"
          placeholder="🔍  Search shows..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          style={{
            padding: '10px 14px',
            borderRadius: '6px',
            border: '1px solid #e2e8f0',
            fontSize: '14px',
            outline: 'none',
            width: '280px',
            color: '#1a1a2e'
          }}
        />
        {searchText && (
          <button
            onClick={() => setSearchText('')}
            style={{
              background: 'none',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              padding: '10px 12px',
              fontSize: '13px',
              color: '#718096',
              cursor: 'pointer'
            }}
          >
            Clear
          </button>
        )}
        {searchText && (
          <p style={{ color: '#718096', fontSize: '13px' }}>
            {filteredShows.length} result{filteredShows.length !== 1 ? 's' : ''}
          </p>
        )}
        {currentUser?.role !== 'viewer' && (
          <button
            onClick={() => setShowDeletedModal(true)}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              padding: '10px 16px',
              fontSize: '13px',
              color: '#718096',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            🗑️ Deleted Shows
          </button>
        )}
      </div>

      {/* Shows Grid */}
      {shows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px 0' }}>
          <p style={{ fontSize: '48px', marginBottom: '16px' }}>🎬</p>
          <h3 style={{ color: '#1a1a2e', marginBottom: '8px' }}>No shows yet</h3>
          <p style={{ color: '#718096' }}>Click "+ New Show" to create your first show.</p>
        </div>
      ) : filteredShows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px 0' }}>
          <p style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</p>
          <h3 style={{ color: '#1a1a2e', marginBottom: '8px' }}>No shows found</h3>
          <p style={{ color: '#718096' }}>No shows match "{searchText}". Try a different search.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
          {filteredShows.map(show => (
            <div
              key={show.id}
              className="card"
              style={{
                border: currentShowId === show.id ? '2px solid #4299e1' : '2px solid transparent',
                position: 'relative',
                padding: '20px'
              }}
            >
              {currentShowId === show.id && (
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  backgroundColor: '#4299e1',
                  color: 'white',
                  fontSize: '11px',
                  fontWeight: '700',
                  padding: '2px 8px',
                  borderRadius: '10px'
                }}>
                  OPEN
                </div>
              )}

              <p style={{ fontSize: '11px', color: '#a0aec0', marginBottom: '2px' }}>
                Created {show.createdAt} {show.ownerName ? `· Owner: ${show.ownerName}` : ''}
              </p>
              {show.lastSaved && (
                <p style={{ fontSize: '11px', color: '#68d391', marginBottom: '6px' }}>
                  Last saved {new Date(show.lastSaved).toLocaleString()}
                </p>
              )}
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1a1a2e', marginBottom: '8px' }}>
                {show.name}
              </h3>

              {show.jobInfo?.showDates && (
                <p style={{ fontSize: '13px', color: '#718096', marginBottom: '4px' }}>
                  📅 {show.jobInfo.showDates}
                </p>
              )}
              {show.presenterOrder?.length > 0 && (
                <p style={{ fontSize: '13px', color: '#718096', marginBottom: '4px' }}>
                  🎤 {show.presenterOrder.length} presenter{show.presenterOrder.length !== 1 ? 's' : ''}
                </p>
              )}
              {show.showFlowRows?.length > 0 && (
                <p style={{ fontSize: '13px', color: '#718096', marginBottom: '16px' }}>
                  📋 {show.showFlowRows.length} show flow row{show.showFlowRows.length !== 1 ? 's' : ''}
                </p>
              )}

              <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <button
                  onClick={() => onOpenShow(show.id)}
                  style={{
                    flex: 1,
                    backgroundColor: '#4299e1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Open Show
                </button>
                {(currentUser?.role === 'admin' || show.userPermission === 'owner') && (
                  <button
                    onClick={() => { setRenamingShow(show.id); setRenameValue(show.name); }}
                    title="Rename show"
                    style={{
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #c6f6d5',
                      background: '#f0fff4',
                      fontSize: '13px',
                      cursor: 'pointer',
                      color: '#276749',
                      fontWeight: '500'
                    }}
                  >
                    ✏️
                  </button>
                )}
                {currentUser?.role !== 'viewer' && (
                  <button
                    onClick={() => onDuplicateShow(show.id)}
                    title="Duplicate show"
                    style={{
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #bee3f8',
                      background: '#ebf8ff',
                      fontSize: '13px',
                      cursor: 'pointer',
                      color: '#2b6cb0',
                      fontWeight: '500'
                    }}
                  >
                    📋
                  </button>
                )}
                {(currentUser?.role === 'admin' || show.userPermission === 'owner') && (
                  <button
                    onClick={() => setDeleteConfirm(show.id)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #fed7d7',
                      background: '#fff5f5',
                      fontSize: '13px',
                      cursor: 'pointer',
                      color: '#c53030',
                      fontWeight: '500'
                    }}
                  >
                    🗑️
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
{showDeletedModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ width: '640px', maxHeight: '80vh', overflowY: 'auto' }}>
            <DeletedShowsPanel onClose={() => setShowDeletedModal(false)} />
          </div>
        </div>
      )}
      {/* New Show Modal */}
      {showNewModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ width: '440px' }}>
            <h2 style={{ marginBottom: '20px', color: '#1a1a2e', fontSize: '20px' }}>
              Create New Show
            </h2>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#4a5568', marginBottom: '6px' }}>
              Show Name *
            </label>
            <input
              type="text"
              autoFocus
              placeholder="e.g. Acme Corp Annual Conference 2026"
              value={newShowName}
              onChange={e => setNewShowName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setShowNewModal(false); }}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
                fontSize: '14px',
                outline: 'none',
                color: '#1a1a2e',
                marginBottom: '20px',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => { setShowNewModal(false); setNewShowName(''); }}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                  background: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!newShowName.trim()}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '6px',
                  border: 'none',
                  background: newShowName.trim() ? '#4299e1' : '#a0aec0',
                  color: 'white',
                  cursor: newShowName.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Create Show
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Modal */}
      {renamingShow && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ width: '440px' }}>
            <h2 style={{ marginBottom: '20px', color: '#1a1a2e', fontSize: '20px' }}>
              Rename Show
            </h2>
            <input
              type="text"
              autoFocus
              value={renameValue}
              onChange={e => setRenameValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleRename();
                if (e.key === 'Escape') setRenamingShow(null);
              }}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
                fontSize: '14px',
                outline: 'none',
                color: '#1a1a2e',
                marginBottom: '20px',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setRenamingShow(null)}
                style={{
                  flex: 1, padding: '10px', borderRadius: '6px',
                  border: '1px solid #e2e8f0', background: 'white',
                  cursor: 'pointer', fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleRename}
                disabled={!renameValue.trim()}
                style={{
                  flex: 1, padding: '10px', borderRadius: '6px',
                  border: 'none',
                  background: renameValue.trim() ? '#4299e1' : '#a0aec0',
                  color: 'white',
                  cursor: renameValue.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '14px', fontWeight: '600'
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ width: '400px', textAlign: 'center' }}>
            <p style={{ fontSize: '40px', marginBottom: '12px' }}>🗑️</p>
            <h3 style={{ marginBottom: '8px', color: '#1a1a2e' }}>Delete Show?</h3>
            <p style={{ color: '#718096', fontSize: '14px', marginBottom: '24px' }}>
              This show will be moved to Deleted Shows, where you can restore it later if needed.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{
                  padding: '10px 24px',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                  background: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => { onDeleteShow(deleteConfirm); setDeleteConfirm(null); }}
                style={{
                  padding: '10px 24px',
                  borderRadius: '6px',
                  border: 'none',
                  background: '#e53e3e',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DeletedShowsPanel({ onClose }) {
  const [deletedShows, setDeletedShows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.getDeletedShows();
      setDeletedShows(Array.isArray(data) ? data : []);
    } catch (err) {
      setMessage('Could not load deleted shows.');
    }
    setLoading(false);
  };

  React.useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const handleRestore = async (id, name) => {
    if (!window.confirm(`Restore "${name}"? It will reappear in your shows list.`)) return;
    const result = await api.undeleteShow(id);
    if (result.error) { setMessage(result.error); return; }
    setMessage(`Restored "${name}". Reloading...`);
    setTimeout(() => window.location.reload(), 1000);
  };

  const handlePermanent = async (id, name) => {
    if (!window.confirm(`PERMANENTLY delete "${name}"?\n\nThis cannot be undone. All rows, presenters, and version history will be erased.`)) return;
    const result = await api.permanentlyDeleteShow(id);
    if (result.error) { setMessage(result.error); return; }
    setMessage(`Permanently deleted "${name}".`);
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1a1a2e' }}>
          🗑️ Deleted Shows
        </h3>
        <button
          onClick={onClose}
          style={{
            background: 'none', border: '1px solid #e2e8f0', borderRadius: '6px',
            padding: '6px 14px', fontSize: '13px', cursor: 'pointer', color: '#4a5568'
          }}
        >
          Close
        </button>
      </div>
      <p style={{ fontSize: '13px', color: '#718096', marginBottom: '14px' }}>
        Deleted shows are hidden but retained. You can restore shows you own, or remove them permanently.
      </p>

      {message && (
        <div style={{
          backgroundColor: '#ebf8ff', border: '1px solid #bee3f8', borderRadius: '6px',
          padding: '10px', marginBottom: '12px', color: '#2b6cb0', fontSize: '13px'
        }}>
          {message}
        </div>
      )}

      <div style={{ marginTop: '14px' }}>
          {loading ? (
            <p style={{ fontSize: '13px', color: '#718096' }}>Loading...</p>
          ) : deletedShows.length === 0 ? (
            <p style={{ fontSize: '13px', color: '#a0aec0', fontStyle: 'italic' }}>
              No deleted shows.
            </p>
          ) : (
            deletedShows.map(show => (
              <div key={show.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 12px', borderRadius: '6px', border: '1px solid #e2e8f0',
                marginBottom: '8px', backgroundColor: '#f7fafc'
              }}>
                <div>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a2e' }}>{show.name}</span>
                  <span style={{ fontSize: '12px', color: '#718096', marginLeft: '10px' }}>
                    Deleted {new Date(show.deleted_at).toLocaleString()}
                    {show.deleted_by_name ? ` by ${show.deleted_by_name}` : ''}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleRestore(show.id, show.name)}
                    style={{
                      background: '#f0fff4', border: '1px solid #c6f6d5', borderRadius: '4px',
                      padding: '4px 10px', color: '#276749', cursor: 'pointer', fontSize: '12px', fontWeight: '600'
                    }}
                  >
                    Restore
                  </button>
                  <button
                    onClick={() => handlePermanent(show.id, show.name)}
                    style={{
                      background: '#fff5f5', border: '1px solid #fed7d7', borderRadius: '4px',
                      padding: '4px 10px', color: '#c53030', cursor: 'pointer', fontSize: '12px', fontWeight: '600'
                    }}
                  >
                    Delete Forever
                  </button>
                </div>
              </div>
            ))
          )}
      </div>
    </div>
  );
}

export default SavedShows;